import { createFileRoute, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";

import { PublicUserProfile } from "@/components/views/users/public-user-profile";
import { getPublicRecipes } from "@/lib/api/getPublicRecipes.server";
import {
  getPublicRecipeCountForUser,
  getPublicUserProfile,
} from "@/lib/api/getPublicUserProfile.server";
import { auth } from "@/lib/auth";

const PROFILE_RECIPES_LIMIT = 20;

const getProfileData = createServerFn({ method: "GET" })
  .inputValidator((data: { userId: string }) => data)
  .handler(async ({ data }) => {
    const request = getRequest();
    const session = await auth.api.getSession({ headers: request.headers });
    const sessionUserId = session?.user?.id ?? null;

    const profile = await getPublicUserProfile(data.userId);
    if (!profile) return null;

    const [recipesResponse, publicRecipeCount] = await Promise.all([
      getPublicRecipes({
        createdBy: data.userId,
        userId: sessionUserId,
        limit: PROFILE_RECIPES_LIMIT,
      }),
      getPublicRecipeCountForUser(data.userId),
    ]);

    return {
      profile: {
        id: profile.id,
        name: profile.name,
        image: profile.image,
        createdAt: profile.createdAt.toISOString(),
      },
      recipesResponse,
      publicRecipeCount,
      isOwnProfile: sessionUserId === profile.id,
    };
  });

export const Route = createFileRoute("/users/$userId/")({
  loader: async ({ params }) => {
    const data = await getProfileData({ data: { userId: params.userId } });
    if (!data) throw notFound();
    return data;
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    return {
      meta: [
        { title: `${loaderData.profile.name} - Scoopulator` },
        {
          name: "description",
          content: `View public ice cream recipes shared by ${loaderData.profile.name} on Scoopulator.`,
        },
      ],
    };
  },
  component: UserProfilePage,
});

function UserProfilePage() {
  return <PublicUserProfile {...Route.useLoaderData()} />;
}
